import axios from 'axios'
import { apiClient } from '@/api/client'
import {
  companyShiftsPath,
  companyShiftsTemplatesPath,
  companyShiftByIdPath,
  companyShiftAssignmentsPath,
  companyShiftAssignmentsBulkPath,
  companyShiftAssignmentByIdPath,
  companyShiftAssignmentConfirmPath,
  companyShiftAssignmentCancelPath,
  companyShiftAssignmentsRecentCustomSlotsPath,
  companyShiftRotaUploadPreviewPath,
  companyShiftRotaUploadCommitPath,
  companyShiftAlertByIdPath,
  companyShiftAlertAcknowledgePath,
  SHIFTS_OVERVIEW,
  SHIFT_ASSIGNMENTS_OVERVIEW,
  SHIFT_ASSIGNMENTS_ME,
  SHIFTS_ROSTER,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type { PaginatedResponse } from '@/types/common.types'
import type {
  Shift,
  ShiftAssignment,
  ShiftFilterQueryDto,
  ShiftAssignmentFilterQueryDto,
  CreateShiftDto,
  UpdateShiftDto,
  CreateShiftAssignmentDto,
  UpdateShiftAssignmentDto,
  BulkAssignShiftDto,
  BulkAssignShiftResult,
  CancelShiftAssignmentDto,
  ShiftAlertDetail,
  RecentCustomShiftSlot,
  RotaConflictPolicy,
  RotaPreviewResult,
  RotaCommitResult,
} from '@/types/shift.types'

interface PaginatedPayload<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages?: number
}

function toPaginatedResponse<T>(inner: PaginatedPayload<T>): PaginatedResponse<T> {
  const total = Number(inner.total) || 0
  const limit = Number(inner.limit) || 10
  const totalPages =
    inner.totalPages != null && inner.totalPages > 0
      ? inner.totalPages
      : Math.ceil(total / limit) || 0
  return {
    data: Array.isArray(inner.data) ? inner.data : [],
    total,
    page: Number(inner.page) || 1,
    limit,
    totalPages,
  }
}

export async function createShift(companyId: string, payload: CreateShiftDto): Promise<Shift> {
  const { data } = await apiClient.post<ApiResponseWrapper<Shift>>(
    companyShiftsPath(companyId),
    payload
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Failed to create shift')
}

export async function getShifts(
  companyId: string,
  params?: ShiftFilterQueryDto
): Promise<PaginatedResponse<Shift>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedPayload<Shift>>>(
    companyShiftsPath(companyId),
    { params }
  )
  const payload = data?.data
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return toPaginatedResponse(payload)
  }
  return toPaginatedResponse({
    data: [],
    total: 0,
    page: 1,
    limit: params?.limit ?? 10,
  })
}

export async function getShiftTemplates(companyId: string): Promise<Shift[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<Shift[]>>(
    companyShiftsTemplatesPath(companyId)
  )
  if (data?.data && Array.isArray(data.data)) {
    return data.data
  }
  return []
}

export async function getShiftById(companyId: string, id: string): Promise<Shift> {
  const { data } = await apiClient.get<ApiResponseWrapper<Shift>>(
    companyShiftByIdPath(companyId, id)
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Shift not found')
}

export async function updateShift(
  companyId: string,
  id: string,
  payload: UpdateShiftDto
): Promise<Shift> {
  const { data } = await apiClient.put<ApiResponseWrapper<Shift>>(
    companyShiftByIdPath(companyId, id),
    payload
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Failed to update shift')
}

export async function deleteShift(companyId: string, id: string): Promise<void> {
  await apiClient.delete(companyShiftByIdPath(companyId, id))
}

export async function createShiftAssignment(
  companyId: string,
  payload: CreateShiftAssignmentDto
): Promise<ShiftAssignment> {
  const { data } = await apiClient.post<ApiResponseWrapper<ShiftAssignment>>(
    companyShiftAssignmentsPath(companyId),
    payload
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Failed to create shift assignment')
}

export async function bulkAssignShifts(
  companyId: string,
  payload: BulkAssignShiftDto
): Promise<BulkAssignShiftResult> {
  const { data } = await apiClient.post<ApiResponseWrapper<Partial<BulkAssignShiftResult>>>(
    companyShiftAssignmentsBulkPath(companyId),
    payload
  )
  if (data?.data && typeof data.data === 'object') {
    return {
      success: data.success,
      statusCode: data.statusCode,
      created: Number(data.data.created ?? 0),
      skipped: Number(data.data.skipped ?? 0),
      errors: Array.isArray(data.data.errors) ? data.data.errors : [],
      summary:
        data.data.summary && typeof data.data.summary === 'object'
          ? {
              totalRequested: Number(data.data.summary.totalRequested ?? 0),
              totalCreated: Number(data.data.summary.totalCreated ?? data.data.created ?? 0),
              totalFailed: Number(data.data.summary.totalFailed ?? data.data.errors?.length ?? 0),
              totalSkipped: Number(data.data.summary.totalSkipped ?? data.data.skipped ?? 0),
            }
          : undefined,
    }
  }
  return { created: 0, skipped: 0, errors: [] }
}

export async function getShiftAssignments(
  companyId: string,
  params?: ShiftAssignmentFilterQueryDto
): Promise<PaginatedResponse<ShiftAssignment>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedPayload<ShiftAssignment>>>(
    companyShiftAssignmentsPath(companyId),
    { params }
  )
  const payload = data?.data
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return toPaginatedResponse(payload)
  }
  return toPaginatedResponse({
    data: [],
    total: 0,
    page: 1,
    limit: params?.limit ?? 10,
  })
}

interface ShiftAssignmentsMePayload {
  data: ShiftAssignment[]
  total: number
  page?: number
  limit?: number
}

interface FlattenedShiftAssignmentsPayload {
  data: ShiftAssignment[]
  total?: number
  page?: number
  limit?: number
}

function toShiftAssignmentsResponse(
  response: ApiResponseWrapper<unknown> & Partial<FlattenedShiftAssignmentsPayload>,
  fallbackLimit: number
): PaginatedResponse<ShiftAssignment> {
  if (Array.isArray(response.data)) {
    return toPaginatedResponse({
      data: response.data,
      total: Number(response.total) || response.data.length,
      page: response.page ?? 1,
      limit: response.limit ?? fallbackLimit,
    })
  }

  const payload = response.data as Partial<ShiftAssignmentsMePayload> | undefined
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    const limit = payload.limit ?? fallbackLimit
    const page = payload.page ?? 1
    return toPaginatedResponse({
      data: payload.data,
      total: Number(payload.total) || 0,
      page,
      limit,
    })
  }

  return toPaginatedResponse({
    data: [],
    total: 0,
    page: 1,
    limit: fallbackLimit,
  })
}

function getClientTimeContext(): Pick<
  ShiftAssignmentFilterQueryDto,
  'timezone' | 'clientTimezoneOffsetMinutes'
> {
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    clientTimezoneOffsetMinutes: new Date().getTimezoneOffset(),
  }
}

function withClientTimeContext<T extends ShiftAssignmentFilterQueryDto>(
  params?: T
): T & Pick<ShiftAssignmentFilterQueryDto, 'timezone' | 'clientTimezoneOffsetMinutes'> {
  return {
    ...getClientTimeContext(),
    ...(params ?? {}),
  } as T & Pick<ShiftAssignmentFilterQueryDto, 'timezone' | 'clientTimezoneOffsetMinutes'>
}

export async function getMyShiftAssignments(
  companyId: string,
  params?: ShiftAssignmentFilterQueryDto
): Promise<PaginatedResponse<ShiftAssignment>> {
  const { data } = await apiClient.get<
    ApiResponseWrapper<ShiftAssignmentsMePayload | ShiftAssignment[]> &
      Partial<FlattenedShiftAssignmentsPayload>
  >(SHIFT_ASSIGNMENTS_ME, {
    params: withCompanyIds(withClientTimeContext({ ...(params ?? {}), companyIds: [companyId] })),
  })
  return toShiftAssignmentsResponse(data, params?.limit ?? 10)
}

export async function getRecentCustomShiftSlots(
  companyId: string
): Promise<RecentCustomShiftSlot[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<RecentCustomShiftSlot[]>>(
    companyShiftAssignmentsRecentCustomSlotsPath(companyId)
  )
  if (data?.data && Array.isArray(data.data)) {
    return data.data.map((slot) => ({
      ...slot,
      name: slot.name || slot.slotName || 'Custom slot',
    }))
  }
  return []
}

export async function getShiftAssignmentById(
  companyId: string,
  id: string
): Promise<ShiftAssignment> {
  const { data } = await apiClient.get<ApiResponseWrapper<ShiftAssignment>>(
    companyShiftAssignmentByIdPath(companyId, id)
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Shift assignment not found')
}

export async function updateShiftAssignment(
  companyId: string,
  id: string,
  payload: UpdateShiftAssignmentDto
): Promise<ShiftAssignment> {
  const { data } = await apiClient.put<ApiResponseWrapper<ShiftAssignment>>(
    companyShiftAssignmentByIdPath(companyId, id),
    payload
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Failed to update shift assignment')
}

export async function confirmShiftAssignment(
  companyId: string,
  id: string
): Promise<ShiftAssignment> {
  const { data } = await apiClient.put<ApiResponseWrapper<ShiftAssignment>>(
    companyShiftAssignmentConfirmPath(companyId, id)
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Failed to confirm shift assignment')
}

function shiftAssignmentIdFromAlertDetail(detail: ShiftAlertDetail): string | undefined {
  if (typeof detail.shiftAssignmentId === 'string' && detail.shiftAssignmentId.length > 0) {
    return detail.shiftAssignmentId
  }
  const nested = detail.shiftAssignment?._id
  if (typeof nested === 'string' && nested.length > 0) return nested
  return undefined
}

/** GET shift alert (resolves assignment id when notification metadata omits it). */
export async function getShiftAlertById(
  companyId: string,
  alertId: string
): Promise<ShiftAlertDetail | null> {
  try {
    const { data } = await apiClient.get<ApiResponseWrapper<ShiftAlertDetail>>(
      companyShiftAlertByIdPath(companyId, alertId)
    )
    const raw = data?.data
    if (raw && typeof raw === 'object' && '_id' in raw) {
      return raw as ShiftAlertDetail
    }
    return null
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response?.status === 404) {
      return null
    }
    throw e
  }
}

/**
 * Finds company + shift assignment for an alert by probing companies (when metadata lacks ids).
 */
export async function resolveShiftAlertContext(
  alertId: string,
  companyIds: string[]
): Promise<{ companyId: string; shiftAssignmentId: string } | null> {
  for (const cid of companyIds) {
    const detail = await getShiftAlertById(cid, alertId)
    if (!detail) continue
    const shiftAssignmentId = shiftAssignmentIdFromAlertDetail(detail)
    if (shiftAssignmentId) {
      return { companyId: cid, shiftAssignmentId }
    }
  }
  return null
}

export async function acknowledgeShiftAlert(companyId: string, alertId: string): Promise<void> {
  await apiClient.post(companyShiftAlertAcknowledgePath(companyId, alertId))
}

export async function cancelShiftAssignment(
  companyId: string,
  id: string,
  payload: CancelShiftAssignmentDto
): Promise<ShiftAssignment> {
  const { data } = await apiClient.put<ApiResponseWrapper<ShiftAssignment>>(
    companyShiftAssignmentCancelPath(companyId, id),
    payload
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Failed to cancel shift assignment')
}

export async function deleteShiftAssignment(companyId: string, id: string): Promise<void> {
  await apiClient.delete(companyShiftAssignmentByIdPath(companyId, id))
}

// ── Cross-company aggregate fetchers ──────────────────────────────────────

export interface CrossCompanyShiftQuery extends ShiftFilterQueryDto {
  companyIds?: string[]
}

export interface CrossCompanyShiftAssignmentQuery extends ShiftAssignmentFilterQueryDto {
  companyIds?: string[]
}

function withCompanyIds<T extends { companyIds?: string[] }>(query?: T): Record<string, unknown> {
  if (!query) return {}
  const { companyIds, roleIds, ...rest } = query as T & { roleIds?: string[] }
  const out: Record<string, unknown> = { ...rest }
  if (companyIds && companyIds.length > 0) {
    out.companyIds = companyIds.join(',')
  }
  if (roleIds && roleIds.length > 0) {
    out.roleIds = roleIds.join(',')
  }
  return out
}

export async function getShiftsOverview(
  query?: CrossCompanyShiftQuery
): Promise<PaginatedResponse<Shift>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedPayload<Shift>>>(
    SHIFTS_OVERVIEW,
    { params: withCompanyIds(query) }
  )
  const payload = data?.data
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return toPaginatedResponse(payload)
  }
  return toPaginatedResponse({ data: [], total: 0, page: 1, limit: query?.limit ?? 10 })
}

/**
 * Every shift template for the given companies. The overview pages at 100 (the
 * API's maximum), and an org with dozens of companies has more templates than
 * that, so the rest are fetched in parallel after the first page.
 */
export async function getAllShiftsOverview(
  query?: Omit<CrossCompanyShiftQuery, 'page' | 'limit'>
): Promise<Shift[]> {
  const limit = 100
  const first = await getShiftsOverview({ ...query, page: 1, limit })
  if (first.totalPages <= 1) return first.data
  const rest = await Promise.all(
    Array.from({ length: first.totalPages - 1 }, (_, i) =>
      getShiftsOverview({ ...query, page: i + 2, limit })
    )
  )
  return [first, ...rest].flatMap((page) => page.data)
}

export async function getShiftAssignmentsOverview(
  query?: CrossCompanyShiftAssignmentQuery
): Promise<PaginatedResponse<ShiftAssignment>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedPayload<ShiftAssignment>>>(
    SHIFT_ASSIGNMENTS_OVERVIEW,
    { params: withCompanyIds(query) }
  )
  const payload = data?.data
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return toPaginatedResponse(payload)
  }
  return toPaginatedResponse({ data: [], total: 0, page: 1, limit: query?.limit ?? 10 })
}

export async function getMyShiftAssignmentsOverview(
  query?: CrossCompanyShiftAssignmentQuery
): Promise<PaginatedResponse<ShiftAssignment>> {
  const { data } = await apiClient.get<
    ApiResponseWrapper<PaginatedPayload<ShiftAssignment> | ShiftAssignment[]> &
      Partial<FlattenedShiftAssignmentsPayload>
  >(SHIFT_ASSIGNMENTS_ME, {
    params: withCompanyIds(withClientTimeContext(query)),
  })
  return toShiftAssignmentsResponse(data, query?.limit ?? 10)
}

export interface RosterMember {
  companyId: string
  userId: string
  firstName?: string
  lastName?: string
  email?: string
  employeeId?: string
  role?: string
  roleId?: string
  roleName?: string
  roleSlug?: string
}

export interface RosterResponse {
  members: RosterMember[]
  assignments: ShiftAssignment[]
  totalAssignments: number
}

export async function getShiftsRoster(
  query?: CrossCompanyShiftAssignmentQuery
): Promise<RosterResponse> {
  const { data } = await apiClient.get<ApiResponseWrapper<RosterResponse>>(SHIFTS_ROSTER, {
    params: withCompanyIds(query),
  })
  const payload = data?.data
  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray(payload.members) &&
    Array.isArray(payload.assignments)
  ) {
    return {
      members: payload.members,
      assignments: payload.assignments,
      totalAssignments: Number(payload.totalAssignments) || 0,
    }
  }
  return { members: [], assignments: [], totalAssignments: 0 }
}

/**
 * Builds the multipart body for a ROTA upload. The Axios client strips the
 * Content-Type for FormData so the boundary is set automatically.
 */
function buildRotaFormData(
  file: File,
  startDate: string,
  endDate: string,
  conflictPolicy: RotaConflictPolicy = 'replace'
): FormData {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('startDate', startDate)
  formData.append('endDate', endDate)
  formData.append('conflictPolicy', conflictPolicy)
  return formData
}

/** Validates a ROTA workbook server-side and returns the would-be result. No writes. */
export async function previewRotaUpload(
  companyId: string,
  file: File,
  startDate: string,
  endDate: string,
  conflictPolicy: RotaConflictPolicy = 'replace'
): Promise<RotaPreviewResult> {
  const { data } = await apiClient.post<ApiResponseWrapper<RotaPreviewResult>>(
    companyShiftRotaUploadPreviewPath(companyId),
    buildRotaFormData(file, startDate, endDate, conflictPolicy)
  )
  if (!data?.data) {
    throw new Error('Failed to preview ROTA upload')
  }
  return data.data
}

/** Commits a ROTA workbook, replacing existing assignments for the imported employees/dates. */
export async function commitRotaUpload(
  companyId: string,
  file: File,
  startDate: string,
  endDate: string,
  conflictPolicy: RotaConflictPolicy = 'replace'
): Promise<RotaCommitResult> {
  const { data } = await apiClient.post<ApiResponseWrapper<RotaCommitResult>>(
    companyShiftRotaUploadCommitPath(companyId),
    buildRotaFormData(file, startDate, endDate, conflictPolicy)
  )
  if (!data?.data) {
    throw new Error('Failed to commit ROTA upload')
  }
  return data.data
}
